import { type ReactNode, useMemo } from 'react';

import type {
  ActionItem,
  MeetingPrepViewDefinition,
} from '../utils/meetingPrepTypes';
import { MeetingPrepAccordionSection } from './meeting-prep-accordion-section';

interface PreviousInteractionsSectionProps {
  defaultExpanded: boolean;
  metadata?: string;
  badges?: ReactNode;
  viewDefinition: MeetingPrepViewDefinition;
}

/**
 * Previous interactions section — shows action items from the view_definition.
 *
 * Note: The live interaction feed was removed because it queried contact view-state
 * for event types (ahoy_email_sent, ahoy_meeting_created) that only exist on
 * email/meeting entities, not on contacts. The metadata counts (meeting_count,
 * email_count) are computed correctly by the backend during prep generation. A
 * proper implementation should query associated meetings/emails directly rather
 * than contact timeline events.
 */
export function PreviousInteractionsSection({
  defaultExpanded,
  metadata,
  badges,
  viewDefinition,
}: PreviousInteractionsSectionProps) {
  const actionItems: ActionItem[] = useMemo(
    () => viewDefinition.action_items ?? [],
    [viewDefinition.action_items]
  );

  // The parent gates this section on `action_items.length > 0`, so we can
  // safely assume there's at least one item to render.
  return (
    <MeetingPrepAccordionSection
      title='Previous interactions'
      defaultExpanded={defaultExpanded}
      metadata={metadata}
      badges={badges}
      sectionId='previous-interactions'
    >
      <div className='space-y-3'>
        <ActionItemsList items={actionItems} />
      </div>
    </MeetingPrepAccordionSection>
  );
}

interface ActionItemsListProps {
  items: ActionItem[];
}

function ActionItemsList({ items }: ActionItemsListProps) {
  const keyedItems = useMemo(() => {
    const counts = new Map<string, number>();
    return items.map(item => {
      const base = `${item.source_meeting_title ?? ''}-${item.text}`;
      const count = counts.get(base) ?? 0;
      counts.set(base, count + 1);
      return { item, key: count > 0 ? `${base}::${count}` : base };
    });
  }, [items]);

  return (
    <div className='space-y-2'>
      {keyedItems.map(({ item, key }) => (
        <div key={key} className='flex items-start gap-2'>
          <div className='w-4 h-4 rounded border border-[var(--color-border-primary)] shrink-0 mt-0.5' />
          <div className='min-w-0'>
            <span className='text-sm text-[var(--color-text-primary)]'>
              {item.text}
            </span>
            {[item.source_meeting_title, item.source_meeting_date].some(
              Boolean
            ) && (
              <p className='text-xs text-[var(--color-text-tertiary)]'>
                {[item.source_meeting_title, item.source_meeting_date]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
